import moment from 'moment';
import { eventCheckerCalendar } from '../../../settings/opningHours/utils/Functions';

export function generateTimeSlots() {
    const timeSlots = [];
    const startTime = moment('00:00', 'HH:mm');
    const endTime = moment('23:55', 'HH:mm');

    while (startTime <= endTime) {
        const nextTime = moment(startTime).add(30, 'minutes');
        timeSlots.push(`${startTime.format('HH:mm')} - ${nextTime.format('HH:mm')}`);
        startTime.add(5, 'minutes');
    }

    return timeSlots;
}

export const transformServiceList = (data, fields, t) => {
    const filteredData = data.map((group) => ({
        ...group,
        services: group.services.filter((service) => {
            return !fields.some((field) => field.serviceId === service.id);
        }),
    }));

    const groupedServices = filteredData
        .filter((group) => group.id !== 0)
        .map((group) => ({
            title: group.group,
            groupId: group.id,
            data: group.services.length > 0 ? group.services : [],
        }));

    const ungroupedServices = filteredData
        .filter((group) => group.id === 0)
        .flatMap((group) => group.services)
        .map((service) => ({
            ...service,
            groupId: 0,
        }));

    return [
        {
            title: t('Calendar.ServicesWithoutGroup'),
            groupId: 0,
            data: ungroupedServices.length > 0 ? ungroupedServices : [],
        },
        ...groupedServices,
    ];
};

export function getItems(duration) {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    if (hours > 0) {
        return `${hours}t. ${minutes}min.`;
    } else {
        return `${minutes}min.`;
    }
}

export const formatPhoneNumber = (number) => {
    if (number != '') {
        return number?.replace(/(\d{2})(?=\d)/g, '$1 ');
    } else {
        return '';
    }
};

export const CountryCodeGetter = ({ code = '+45' }) => {
    const countryCode = code?.replace('+', '');
    if (code) {
        return Number(countryCode);
    } else {
        return 45;
    }
};

export const getEffectiveInspectionCharges = (inspectionData) => {
    const chargesData = inspectionData?.chargesData || {};
    const overrideChargesData = inspectionData?.overrideChargesData;

    const isTravelOverridden = Boolean(
        overrideChargesData?.is_travel_fees_overridden ?? inspectionData?.is_travel_fees_overridden,
    );
    const isBridgeOverridden = Boolean(
        overrideChargesData?.is_bridge_fees_overridden ?? inspectionData?.is_bridge_fees_overridden,
    );

    const travelFees = isTravelOverridden
        ? Number(overrideChargesData?.travelFees) || 0
        : Number(chargesData.travelFees) || 0;
    const bridgeFees = isBridgeOverridden
        ? Number(overrideChargesData?.bridgeFees) || 0
        : Number(chargesData.bridgeFees) || 0;

    return {
        travelFees,
        bridgeFees,
        totalCharges: travelFees + bridgeFees,
        is_travel_fees_overridden: isTravelOverridden,
        is_bridge_fees_overridden: isBridgeOverridden,
    };
};

export const stripLegacyInspectionOverrideFlags = (inspectionData = {}) => {
    const { is_travel_fees_overridden, is_bridge_fees_overridden, ...rest } = inspectionData;
    return rest;
};

export const buildMergedInspectionOverrideChargesData = ({
    originalInspectionData,
    currentInspectionData,
    resetOverrides = false,
}) => {
    if (resetOverrides) {
        return null;
    }

    const originalOverride = originalInspectionData?.overrideChargesData || {};
    const originalTravelFlag = originalInspectionData
        ? Boolean(originalOverride?.is_travel_fees_overridden ?? originalInspectionData?.is_travel_fees_overridden)
        : false;
    const originalBridgeFlag = originalInspectionData
        ? Boolean(originalOverride?.is_bridge_fees_overridden ?? originalInspectionData?.is_bridge_fees_overridden)
        : false;

    const currentOverride = currentInspectionData?.overrideChargesData || {};
    const currentTravelFlag = Boolean(currentOverride?.is_travel_fees_overridden);
    const currentBridgeFlag = Boolean(currentOverride?.is_bridge_fees_overridden);

    const travelOverridden = currentTravelFlag || originalTravelFlag;
    const bridgeOverridden = currentBridgeFlag || originalBridgeFlag;

    if (!travelOverridden && !bridgeOverridden) {
        return null;
    }

    const overrideChargesData = {
        is_travel_fees_overridden: travelOverridden,
        is_bridge_fees_overridden: bridgeOverridden,
    };

    if (travelOverridden) {
        overrideChargesData.travelFees = currentTravelFlag
            ? Number(currentOverride.travelFees) || 0
            : Number(originalOverride.travelFees) || 0;
    }
    if (bridgeOverridden) {
        overrideChargesData.bridgeFees = currentBridgeFlag
            ? Number(currentOverride.bridgeFees) || 0
            : Number(originalOverride.bridgeFees) || 0;
    }

    return overrideChargesData;
};

export const getMergedEffectiveInspectionCharges = (inspectionData, originalInspectionData = null) => {
    if (!inspectionData) {
        return {
            travelFees: 0,
            bridgeFees: 0,
            totalCharges: 0,
            is_travel_fees_overridden: false,
            is_bridge_fees_overridden: false,
        };
    }

    const overrideChargesData = buildMergedInspectionOverrideChargesData({
        originalInspectionData,
        currentInspectionData: inspectionData,
    });

    return getEffectiveInspectionCharges({
        ...inspectionData,
        overrideChargesData,
    });
};

export const normalizeInspectionOverrideData = (inspectionData, originalInspectionData = null) => {
    if (!inspectionData) {
        return inspectionData;
    }

    const stripped = stripLegacyInspectionOverrideFlags(inspectionData);
    const overrideChargesData = buildMergedInspectionOverrideChargesData({
        originalInspectionData,
        currentInspectionData: inspectionData,
    });

    return {
        ...stripped,
        overrideChargesData,
    };
};

export const validateBooking = (cardValues) => {
    for (let i = 0; i < cardValues.length; i++) {
        const { selectedEmployee, available_time, duration_min } = cardValues[i];
        const startTime = moment(available_time, 'HH:mm'); // Parse available time
        const endTime = moment(startTime).add(duration_min, 'minutes'); // Add duration to get end time

        for (let j = 0; j < cardValues.length; j++) {
            if (i !== j) {
                const {
                    selectedEmployee: compareEmployee,
                    available_time: compareTime,
                    duration_min: compareDuration,
                } = cardValues[j];

                if (selectedEmployee.id === compareEmployee.id) {
                    const compareStartTime = moment(compareTime, 'HH:mm');
                    const compareEndTime = moment(compareStartTime).add(compareDuration, 'minutes');

                    if (
                        (startTime.isBefore(compareEndTime) && endTime.isAfter(compareStartTime)) || // If times overlap
                        (compareStartTime.isBefore(endTime) && compareEndTime.isAfter(startTime)) // If other booking times overlap this
                    ) {
                        return false; // If overlap found, return false
                    }
                }
            }
        }
    }

    return true; // No overlaps, return true
};

export const scrollToHour = ({ settings, selectedDate, selectedEmployee }) => {
    const getSafeScrollTime = (timeStr) => {
        const dateStr = selectedDate.locale('en-gb').format('YYYY-MM-DD'); // Ensure consistent format
        const fullTime = moment(`${dateStr} ${timeStr.padEnd(8, ':00')}`, 'YYYY-MM-DD HH:mm:ss');
        const scrolled = fullTime.clone().subtract(2, 'hours');

        const start = scrolled.isBefore(fullTime.clone().startOf('day')) ? fullTime.clone().startOf('day') : scrolled;

        return `${start.format('HH:mm')} - ${start.clone().add(30, 'minutes').format('HH:mm')}`;
    };

    if (!settings?.is_individual_opening_hour) {
        const day = settings?.schedule?.find((d) => d?.day === selectedDate.locale('en-gb').format('dddd'));
        if (day?.open_time) {
            return getSafeScrollTime(day?.open_time);
        }
    } else if (settings?.is_individual_opening_hour) {
        const emp = settings?.employees_opening_hour?.find((e) => e?.id === selectedEmployee?.id);
        const day = emp?.detail.find((d) => d.start_day === selectedDate.locale('en-gb').format('dddd'));

        const additionalDays = day?.additional_days || {};

        if (
            Object.keys(additionalDays).length > 0 &&
            additionalDays[selectedDate.locale('en-gb').format('YYYY-MM-DD')]
        ) {
            const additional_day = additionalDays[selectedDate.locale('en-gb').format('YYYY-MM-DD')];
            if (additional_day?.start_time) {
                return getSafeScrollTime(additional_day?.start_time);
            }
        } else {
            if (day?.start_time) {
                return getSafeScrollTime(day?.start_time);
            }
        }
    }
};

const fullDayHours = () => {
    const momentStartTime = moment().startOf('day');
    const momentEndTime = moment().endOf('day');
    return {
        momentStartTime,
        momentEndTime,
        open_time: momentStartTime.format('HH:mm'),
        close_time: momentEndTime.format('HH:mm'),
    };
};

const applyCalendarHourPadding = (momentStartTime, momentEndTime, setCalendarOpeningHour, setCalendarClosingHour) => {
    if (!momentStartTime || !momentEndTime) {
        return {
            momentStartTime,
            momentEndTime,
            open_time: momentStartTime?.format('HH:mm'),
            close_time: momentEndTime?.format('HH:mm'),
        };
    }

    // Clone moments before mutating to avoid side effects
    const startTimeClone = momentStartTime.clone();
    const endTimeClone = momentEndTime.clone();

    // Day boundaries
    const dayStart = startTimeClone.clone().startOf('day'); // 00:00:00
    const dayEnd = endTimeClone.clone().endOf('day'); // 23:59:59

    let finalStartTime =
        startTimeClone.format('HH:mm:ss') !== '00:00:00'
            ? startTimeClone.clone().minutes(0).subtract(setCalendarOpeningHour, 'minutes')
            : startTimeClone;

    // Clamp start time (must not go below 00:00:00)
    finalStartTime = moment.max(finalStartTime, dayStart);

    let finalEndTime =
        endTimeClone.format('HH:mm:ss') !== '23:59:59'
            ? endTimeClone.clone().add(setCalendarClosingHour, 'minutes')
            : endTimeClone;

    // Clamp end time (must not go beyond 23:59:59)
    finalEndTime = moment.min(finalEndTime, dayEnd);

    return {
        momentStartTime: finalStartTime,
        momentEndTime: finalEndTime,
        open_time: finalStartTime.format('HH:mm'),
        close_time: finalEndTime.format('HH:mm'),
    };
};

const considerEmployeeSlot = (slot, times) => {
    if (!slot || slot.status === 'off' || !slot.start_time || !slot.end_time) return;

    const start = moment(slot.start_time, 'HH:mm:ss');
    const end = moment(slot.end_time, 'HH:mm:ss');
    if (!start.isValid() || !end.isValid()) return;

    if (!times.momentStartTime || start.isBefore(times.momentStartTime, 'minute')) {
        times.momentStartTime = start;
    }
    if (!times.momentEndTime || end.isAfter(times.momentEndTime, 'minute')) {
        times.momentEndTime = end;
    }
};

const getHoursFromEmployeeOpeningHours = ({
    employees_opening_hour = [],
    selectedDate,
    view,
    employeeIds = [],
}) => {
    const times = { momentStartTime: null, momentEndTime: null };

    const filteredEmployees =
        employeeIds?.length > 0
            ? employees_opening_hour.filter((emp) => employeeIds.some((id) => id == emp?.id))
            : employees_opening_hour;

    // Individual hours ON: employee schedule only — no store fallback.
    if (!filteredEmployees?.length) {
        return fullDayHours();
    }

    // Keep week-day moments on the same locale eventCheckerCalendar will use.
    const prevLocale = moment.locale();
    const matchLocale = localStorage.getItem('language') === 'en' ? 'en-gb' : 'da';
    moment.locale(matchLocale);

    try {
        if (view === 'week') {
            const weekStart = moment(selectedDate).startOf('week');
            for (let i = 0; i < 7; i++) {
                const day = weekStart.clone().add(i, 'day');
                filteredEmployees.forEach((emp) => {
                    considerEmployeeSlot(eventCheckerCalendar(emp, day), times);
                });
            }
        } else {
            const day = moment(selectedDate);
            filteredEmployees.forEach((emp) => {
                considerEmployeeSlot(eventCheckerCalendar(emp, day), times);
            });
        }
    } finally {
        moment.locale(prevLocale);
    }

    // Selected employee(s) have no working hours in range → show full day (slots are grayed out).
    if (!times.momentStartTime || !times.momentEndTime) {
        return fullDayHours();
    }

    return times;
};

const hasNoStoreHours = (dayObj) =>
    dayObj.is_closed ||
    moment(dayObj.open_time, 'HH:mm:ss').isSame(moment(dayObj.close_time, 'HH:mm:ss'), 'minute');

const getHoursFromStoreSchedule = ({ schedule = [], selectedDate, view }) => {
    let momentStartTime = null;
    let momentEndTime = null;

    if (!schedule.length) {
        return fullDayHours();
    }

    if (view === 'week') {
        // Filter out closed / zero-length (e.g. 00:00–00:00) days before calculating min/max
        schedule
            .filter((dayObj) => !hasNoStoreHours(dayObj))
            .forEach((dayObj) => {
                if (momentStartTime) {
                    if (momentStartTime.isAfter(moment(dayObj.open_time, 'HH:mm:ss'), 'minute')) {
                        momentStartTime = moment(dayObj.open_time, 'HH:mm:ss');
                    }
                } else {
                    momentStartTime = moment(dayObj.open_time, 'HH:mm:ss');
                }

                if (momentEndTime) {
                    if (momentEndTime.isBefore(moment(dayObj.close_time, 'HH:mm:ss'), 'minute')) {
                        momentEndTime = moment(dayObj.close_time, 'HH:mm:ss');
                    }
                } else {
                    momentEndTime = moment(dayObj.close_time, 'HH:mm:ss');
                }
            });

        if (!momentStartTime || !momentEndTime) {
            return fullDayHours();
        }
    } else {
        let currentDay = moment(selectedDate).locale('en-gb').format('dddd');
        let foundDay = schedule.find((dayObj) => dayObj.day === currentDay);

        if (foundDay && hasNoStoreHours(foundDay)) {
            return fullDayHours();
        }

        if (foundDay) {
            momentStartTime = moment(foundDay.open_time, 'HH:mm:ss');
            momentEndTime = moment(foundDay.close_time, 'HH:mm:ss');
        } else {
            return fullDayHours();
        }
    }

    return { momentStartTime, momentEndTime };
};

export const calendarOpeningHours = ({
    data = { schedule: [], calendar: {} },
    selectedDate = moment(),
    view: viewProp,
    employeeIds = [],
}) => {
    const view = viewProp || localStorage.getItem('calendarView') || 'week';
    const {
        schedule = [],
        calendar = {},
        is_individual_opening_hour = false,
        employees_opening_hour = [],
    } = data;
    const { setCalendarOpeningHour = 0, setCalendarClosingHour = 0 } = calendar || {};

    // Priority when is_individual_opening_hour is ON:
    // employee opening hours ONLY — no store schedule; full day if employee has no hours.
    // When OFF: store schedule ONLY.
    const { momentStartTime, momentEndTime } = is_individual_opening_hour
        ? getHoursFromEmployeeOpeningHours({
              employees_opening_hour,
              selectedDate,
              view,
              employeeIds,
          })
        : getHoursFromStoreSchedule({ schedule, selectedDate, view });

    return applyCalendarHourPadding(
        momentStartTime,
        momentEndTime,
        setCalendarOpeningHour,
        setCalendarClosingHour,
    );
};
